import type { CSSProperties, ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { Cuboid, Place, u } from './power-builder'

/*
 * The four level objects, built from the same CSS cuboids as the Power Lab
 * and the Control Center so all three share one material. Sizes are scene
 * units (--u) and colour comes from the level's --hue. `live` runs the
 * motion; the small previews on the level rail render still.
 */

export interface ObjectProps {
  live: boolean
}

/** An animated wrapper inside a Place, keeping the 3D context. */
function Move({ className, style, children }: { className?: string; style?: CSSProperties; children: ReactNode }) {
  return (
    <div className={cn('absolute left-0 top-0 [transform-style:preserve-3d]', className)} style={style}>
      {children}
    </div>
  )
}

/** Glow disc on the floor under every object. */
function Floor({ y, size = 460 }: { y: number; size?: number }) {
  return (
    <Place y={y}>
      <div
        className="lab-floor absolute"
        style={{ width: u(size), height: u(size), marginLeft: u(-size / 2), marginTop: u(-size / 2), transform: 'rotateX(90deg)' }}
      />
    </Place>
  )
}

/** A flat energy ring lying on the floor, turning in its own plane. */
function Ring({ y, size, speed, live, reverse = false, solid = false }: { y: number; size: number; speed: string; live: boolean; reverse?: boolean; solid?: boolean }) {
  return (
    <Place y={y}>
      <div
        className="absolute [transform-style:preserve-3d]"
        style={{ width: u(size), height: u(size), marginLeft: u(-size / 2), marginTop: u(-size / 2), transform: 'rotateX(90deg)' }}
      >
        <span
          className={cn('mode-ring absolute inset-0', solid && 'mode-ring-solid', live && 'fan-spin')}
          style={{ animationDuration: speed, animationDirection: reverse ? 'reverse' : undefined }}
        />
      </div>
    </Place>
  )
}

/** A row of LEDs that boot one after another while live. */
function Leds({ count, live, size = 9 }: { count: number; live: boolean; size?: number }) {
  return (
    <span className="flex items-center" style={{ gap: u(size * 0.7) }}>
      {Array.from({ length: count }, (_, led) => (
        <span
          key={led}
          className={cn('lab-led-on rounded-[2px]', live && 'ctrl-boot')}
          style={{ width: u(size), height: u(size), animationDelay: `${led * 0.12}s` }}
        />
      ))}
    </span>
  )
}

const label = (size: number): CSSProperties => ({ fontSize: u(size), letterSpacing: '0.18em' })

/* ------------------------------------------------------------------ */
/* 01 · Add credit: a slim wallet with a card sliding into its slot     */
/* ------------------------------------------------------------------ */

function CardFace() {
  return (
    <div className="flex h-full flex-col justify-between" style={{ padding: u(14) }}>
      <div className="flex items-start justify-between">
        <span className="lvs-chip block rounded-[3px]" style={{ width: u(34), height: u(26) }} />
        <span className="grid grid-cols-2" style={{ gap: u(3) }}>
          {[0, 1, 2, 3].map((dot) => (
            <span key={dot} className="block bg-background/55" style={{ width: u(7), height: u(7) }} />
          ))}
        </span>
      </div>
      <div className="flex" style={{ gap: u(10) }}>
        {[0, 1, 2, 3].map((group) => (
          <span key={group} className="block rounded-full bg-background/35" style={{ width: u(30), height: u(6) }} />
        ))}
      </div>
    </div>
  )
}

function WalletFace({ live }: { live: boolean }) {
  return (
    <div className="flex h-full flex-col" style={{ padding: u(18), gap: u(12) }}>
      <div className="flex items-center justify-between">
        <span className="font-mono font-bold uppercase text-foreground/85" style={label(12)}>
          Wallet
        </span>
        <span className={cn('lab-led-on rounded-full', live && 'power-pulse')} style={{ width: u(10), height: u(10) }} />
      </div>
      <div className="lvs-display flex flex-1 flex-col justify-center rounded-md" style={{ paddingInline: u(14), gap: u(10) }}>
        <span className="font-mono uppercase text-foreground/60" style={label(10)}>
          Credit
        </span>
        <Leds count={10} live={live} size={12} />
      </div>
      <div className="flex items-center" style={{ gap: u(10) }}>
        <span className="lab-led-on block flex-1 rounded-full" style={{ height: u(5) }} />
        <span className="lvs-hue-text font-mono font-bold uppercase" style={label(10)}>
          + Top up
        </span>
      </div>
    </div>
  )
}

export function WalletObject({ live }: ObjectProps) {
  return (
    <>
      <Floor y={118} />
      <Place y={-60}>
        <Move className={live ? 'lvs-insert' : undefined}>
          <Cuboid w={196} h={120} d={6} front="lvs-card" side="lab-hue-side" top="lab-hue-top" frontContent={<CardFace />} />
        </Move>
      </Place>
      <Place y={30}>
        <Cuboid
          w={260}
          h={170}
          d={44}
          front="lvs-shell"
          side="mode-solid-side"
          top="mode-solid-top"
          frontContent={<WalletFace live={live} />}
          topContent={<span className="absolute inset-x-[12%] top-1/2 block -translate-y-1/2 rounded-full bg-background/80" style={{ height: u(10) }} />}
        />
      </Place>
    </>
  )
}

/* ------------------------------------------------------------------ */
/* 02 · Create a machine: a GPU module and glass panel locking into a   */
/*      compact chassis                                                 */
/* ------------------------------------------------------------------ */

function ChassisFront({ live }: { live: boolean }) {
  return (
    <div className="flex h-full flex-col justify-between" style={{ padding: u(14) }}>
      <div className="flex items-center justify-between">
        <span className="font-mono font-bold uppercase text-foreground/80" style={label(11)}>
          Config
        </span>
        <Leds count={6} live={live} size={8} />
      </div>
      <div className="grid grid-cols-10" style={{ gap: u(4) }}>
        {Array.from({ length: 10 }, (_, vent) => (
          <span key={vent} className="block rounded-[2px] bg-background/70 ring-1 ring-foreground/10" style={{ height: u(16) }} />
        ))}
      </div>
    </div>
  )
}

function GpuFace() {
  return (
    <div className="flex h-full items-center justify-between" style={{ paddingInline: u(12) }}>
      <span className="font-mono font-bold uppercase text-primary-foreground" style={label(11)}>
        GPU
      </span>
      <span className="flex" style={{ gap: u(4) }}>
        {Array.from({ length: 9 }, (_, fin) => (
          <span key={fin} className="block bg-background/35" style={{ width: u(4), height: u(16) }} />
        ))}
      </span>
    </div>
  )
}

function GpuTop({ live }: { live: boolean }) {
  return (
    <div className="flex h-full items-center justify-around">
      {[0, 1].map((fan) => (
        <span
          key={fan}
          className={cn('fan-blades block rounded-full', live && 'fan-spin')}
          style={{ width: u(88), height: u(88), animationDuration: '1.2s' }}
        />
      ))}
    </div>
  )
}

export function MachineObject({ live }: ObjectProps) {
  return (
    <>
      <Floor y={110} />
      <Place y={58}>
        <Cuboid
          w={270}
          h={100}
          d={190}
          front="lvs-shell"
          side="mode-solid-side"
          top="mode-solid-top"
          frontContent={<ChassisFront live={live} />}
          topContent={<span className="lvs-display absolute inset-[12%] block rounded-md" />}
        />
      </Place>
      <Place y={-11}>
        <Move className={live ? 'lvs-assemble' : undefined} style={{ '--fy': u(-120) } as CSSProperties}>
          <Cuboid
            w={220}
            h={38}
            d={136}
            front="lab-hue-bright"
            side="lab-hue-side"
            top="mode-solid-top"
            frontContent={<GpuFace />}
            topContent={<GpuTop live={live} />}
          />
        </Move>
      </Place>
      <Place x={140} y={58}>
        <Move className={live ? 'lvs-assemble' : undefined} style={{ '--fx': u(110), animationDelay: '0.25s' } as CSSProperties}>
          <Cuboid w={6} h={96} d={182} front="mode-glass" side="mode-glass" top="mode-glass" />
        </Move>
      </Place>
    </>
  )
}

/* ------------------------------------------------------------------ */
/* 03 · Start your machine: a tower powering on, lit through its glass  */
/* ------------------------------------------------------------------ */

function TowerFront({ live }: { live: boolean }) {
  return (
    <div className="flex h-full flex-col items-center" style={{ padding: u(16), gap: u(16) }}>
      <span className={cn('lvs-power grid place-items-center', live && 'power-pulse')} style={{ width: u(40), height: u(40), marginTop: u(6) }}>
        <span className="block rounded-full border-background/70" style={{ width: u(16), height: u(16), borderWidth: u(3) }} />
      </span>
      <span className="lvs-display flex w-full items-center justify-center rounded-md" style={{ height: u(26) }}>
        <span className="lvs-hue-text font-mono font-bold uppercase" style={label(10)}>
          Boot
        </span>
      </span>
      <span className="flex flex-1 flex-col justify-center" style={{ gap: u(7) }}>
        {Array.from({ length: 6 }, (_, led) => (
          <span
            key={led}
            className={cn('lab-led-on block rounded-full', live && 'ctrl-boot')}
            style={{ width: u(54), height: u(5), animationDelay: `${(5 - led) * 0.14}s` }}
          />
        ))}
      </span>
      <span className="grid w-full grid-cols-5" style={{ gap: u(4) }}>
        {Array.from({ length: 10 }, (_, vent) => (
          <span key={vent} className="block rounded-[2px] bg-background/70 ring-1 ring-foreground/10" style={{ height: u(8) }} />
        ))}
      </span>
    </div>
  )
}

export function TowerObject({ live }: ObjectProps) {
  return (
    <>
      <Floor y={128} size={480} />
      <Ring y={128} size={300} speed="10s" live={live} />
      <Ring y={128} size={390} speed="16s" live={live} reverse solid />
      <Place y={0}>
        <Cuboid
          w={150}
          h={250}
          d={200}
          front="lvs-shell"
          side="mode-solid-side"
          top="mode-solid-top"
          frontContent={<TowerFront live={live} />}
          topContent={
            <span
              className={cn('fan-blades absolute left-1/2 top-1/2 block -translate-x-1/2 -translate-y-1/2 rounded-full', live && 'fan-spin')}
              style={{ width: u(110), height: u(110), animationDuration: '0.9s' }}
            />
          }
        />
      </Place>
      {/* glass side window, lit from inside */}
      <Place x={77} y={-6}>
        <Cuboid w={2} h={190} d={150} front="mode-glass" side="mode-glass" top="mode-glass" />
      </Place>
    </>
  )
}

/* ------------------------------------------------------------------ */
/* 04 · Connect & play: a monitor showing a remote Windows desktop      */
/* ------------------------------------------------------------------ */

function Desktop({ live }: { live: boolean }) {
  return (
    <div className="h-full" style={{ padding: u(8) }}>
      <div className="lvs-wallpaper relative h-full overflow-hidden rounded-[3px]">
        {/* desktop icons */}
        <span className="absolute flex flex-col" style={{ left: u(10), top: u(10), gap: u(8) }}>
          {[0, 1, 2].map((icon) => (
            <span key={icon} className="block rounded-[3px] bg-foreground/25" style={{ width: u(16), height: u(16) }} />
          ))}
        </span>

        {/* main window: the streamed session */}
        <div className="lvs-window absolute overflow-hidden rounded-md" style={{ left: u(40), top: u(16), width: u(190), height: u(112) }}>
          <div className="flex items-center justify-between bg-background/50" style={{ height: u(14), paddingInline: u(6) }}>
            <span className="flex" style={{ gap: u(3) }}>
              {[0, 1, 2].map((dot) => (
                <span key={dot} className="block rounded-full bg-foreground/40" style={{ width: u(5), height: u(5) }} />
              ))}
            </span>
            <span className="font-mono uppercase text-foreground/70" style={label(7)}>
              Remote desktop
            </span>
          </div>
          <div className="flex items-end" style={{ height: u(98), padding: u(10), gap: u(5) }}>
            {[44, 70, 56, 82, 64, 90, 52, 76, 60, 86].map((height, bar) => (
              <span
                key={bar}
                className={cn('lab-led-on block flex-1 rounded-t-[2px]', live && 'ctrl-boot')}
                style={{ height: `${height}%`, animationDelay: `${bar * 0.1}s` }}
              />
            ))}
          </div>
        </div>

        {/* side panel: link status */}
        <div className="lvs-window absolute rounded-md" style={{ right: u(12), top: u(24), width: u(74), padding: u(8) }}>
          <span className="flex items-center" style={{ gap: u(5) }}>
            <span className={cn('lab-led-on block rounded-full', live && 'power-pulse')} style={{ width: u(7), height: u(7) }} />
            <span className="font-mono font-bold uppercase text-foreground" style={label(7)}>
              Live
            </span>
          </span>
          <span className="flex items-end" style={{ gap: u(3), height: u(20), marginTop: u(6) }}>
            {[30, 55, 80, 100].map((height) => (
              <span key={height} className="lvs-hue-bg block flex-1 rounded-[1px]" style={{ height: `${height}%` }} />
            ))}
          </span>
        </div>

        {/* taskbar */}
        <div className="lvs-taskbar absolute inset-x-0 bottom-0 flex items-center justify-between" style={{ height: u(20), paddingInline: u(8) }}>
          <span className="flex items-center" style={{ gap: u(6) }}>
            <span className="grid grid-cols-2" style={{ gap: u(1.5) }}>
              {[0, 1, 2, 3].map((pane) => (
                <span key={pane} className="lvs-hue-bg block" style={{ width: u(5), height: u(5) }} />
              ))}
            </span>
            {[0, 1, 2, 3].map((app) => (
              <span key={app} className="block rounded-[2px] bg-foreground/30" style={{ width: u(10), height: u(10) }} />
            ))}
          </span>
          <span className="font-mono uppercase text-foreground/80" style={label(7)}>
            Connected
          </span>
        </div>

        {/* pointer */}
        <span className={cn('lvs-cursor absolute block', live && 'lvs-cursor-run')} style={{ left: '62%', top: '56%', width: u(11), height: u(15) }} />
      </div>
    </div>
  )
}

export function MonitorObject({ live }: ObjectProps) {
  return (
    <>
      <Floor y={142} size={480} />
      <Place y={-26}>
        <Cuboid w={330} h={200} d={14} front="lvs-bezel" side="mode-solid-side" top="mode-solid-top" frontContent={<Desktop live={live} />} />
      </Place>
      <Place y={104} z={-18}>
        <Cuboid w={30} h={62} d={12} front="lvs-shell" side="mode-solid-side" top="mode-solid-top" />
      </Place>
      <Place y={137} z={-12}>
        <Cuboid w={150} h={8} d={92} front="lvs-shell" side="mode-solid-side" top="mode-solid-top" />
      </Place>
    </>
  )
}
